import { useEffect, useState, useCallback } from 'react'
import AppleLoginButton from './buttons/AppleLoginButton'
import GoogleLoginButton from './buttons/GoogleLoginButton'
import KakaoLoginButton from './buttons/KakaoLoginButton'
import { isIOS, isAndroid } from '@/utils/webView'

export type Provider = 'apple' | 'google' | 'kakao'

interface LoginButtonsProps {
  onLoginSelect: (provider: Provider) => void
  isLoading?: boolean
  selectedProvider?: Provider | null
  error?: string | null
  onRetry?: (provider: Provider) => void
}

// 초기 플랫폼 감지 함수 - SSR 안전
const getInitialPlatform = (): 'ios' | 'android' | 'web' => {
  if (typeof window === 'undefined') return 'web'
  if (isIOS()) return 'ios'
  if (isAndroid()) return 'android'
  return 'web'
}

export const LoginButtons = ({
  onLoginSelect,
  isLoading = false,
  selectedProvider = null,
  error,
  onRetry
}: LoginButtonsProps) => {
  // 초기값을 즉시 계산하여 설정
  const [platform, setPlatform] = useState<'ios' | 'android' | 'web'>(getInitialPlatform)
  const [isPlatformDetected, setIsPlatformDetected] = useState(false)

  useEffect(() => {
    // 플랫폼 감지 재확인 (hydration 이후)
    const detectedPlatform = getInitialPlatform()
    setPlatform(detectedPlatform)
    setIsPlatformDetected(true)
    console.log('[LoginButtons] 플랫폼 감지 완료:', detectedPlatform)
  }, [])

  const handleAppleLogin = useCallback(() => {
    console.log('[LoginButtons] Apple 로그인 버튼 클릭')
    if (error && onRetry) {
      onRetry('apple')
    } else {
      onLoginSelect('apple')
    }
  }, [error, onRetry, onLoginSelect])

  const handleGoogleLogin = useCallback(() => {
    console.log('[LoginButtons] Google 로그인 버튼 클릭')
    if (error && onRetry) {
      onRetry('google')
    } else {
      onLoginSelect('google')
    }
  }, [error, onRetry, onLoginSelect])

  const handleKakaoLogin = useCallback(() => {
    if (error && onRetry) {
      onRetry('kakao')
    } else {
      onLoginSelect('kakao')
    }
  }, [error, onRetry, onLoginSelect])

  return (
    <div
      className={`px-6 pb-8 space-y-4 noto-sans-kr transition-opacity duration-500 ${
        isPlatformDetected ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {error && (
        <div className='bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm'>{error}</div>
      )}
      <div className='space-y-3'>
        {/* 카카오 로그인 버튼 - 모든 플랫폼에서 표시 */}
        <div className='relative'>
          <KakaoLoginButton handleKakaoLogin={handleKakaoLogin} />
          {isLoading && selectedProvider === 'kakao' && (
            <div className='absolute inset-0 flex items-center justify-center bg-yellow-400 bg-opacity-80 rounded-lg'>
              <svg
                className='animate-spin h-5 w-5 text-black'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                ></path>
              </svg>
            </div>
          )}
        </div>

        {/* 구글 로그인 버튼 - 모든 플랫폼에서 표시 */}
        <div className='relative'>
          <GoogleLoginButton handleGoogleLogin={handleGoogleLogin} isLightMode={true} />
          {isLoading && selectedProvider === 'google' && (
            <div className='absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 rounded-lg border border-gray-300'>
              <svg
                className='animate-spin h-5 w-5 text-gray-600'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                ></path>
              </svg>
            </div>
          )}
        </div>

        {/* 애플 로그인 버튼 - iOS만 표시 (부드러운 전환) */}
        <div
          className={`relative transition-all duration-300 ease-in-out ${
            platform === 'ios'
              ? 'max-h-20 opacity-100 transform translate-y-0'
              : 'max-h-0 opacity-0 transform -translate-y-2 pointer-events-none overflow-hidden'
          }`}
          style={{
            // 플랫폼 감지 전까지는 공간 예약
            minHeight: !isPlatformDetected && platform === 'ios' ? '56px' : undefined
          }}
        >
          <AppleLoginButton handleAppleLogin={handleAppleLogin} isLightMode={true} />
          {isLoading && selectedProvider === 'apple' && (
            <div className='absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg'>
              <svg
                className='animate-spin h-5 w-5 text-white'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
              >
                <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                <path
                  className='opacity-75'
                  fill='currentColor'
                  d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                ></path>
              </svg>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
